import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Download, X, Share, Smartphone } from 'lucide-react';

export const InstallBanner = ({ show, isInstalled, isIOS, isInstallable, promptInstall, onDismiss }) => {
  if (!show || isInstalled) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-3 relative"
        data-testid="install-banner"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="hidden sm:flex w-10 h-10 rounded-full bg-white/20 items-center justify-center flex-shrink-0">
              <Smartphone className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm sm:text-base">
                Add Brioritizer to your {isIOS ? 'home screen' : 'desktop'}
              </p>
              <p className="text-xs sm:text-sm text-white/80 hidden sm:block">
                {isIOS 
                  ? 'Get quick access from your iPhone or iPad home screen'
                  : 'Install the app for a faster, distraction-free experience'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isIOS ? (
              <Button
                size="sm"
                variant="secondary"
                className="bg-white text-emerald-600 hover:bg-white/90 gap-1.5 text-xs sm:text-sm"
                onClick={() => {
                  toast.info(
                    <div className="space-y-2">
                      <p className="font-semibold">Add to Home Screen:</p>
                      <ol className="text-sm space-y-1 list-decimal list-inside">
                        <li>Tap the Share button <Share className="w-3 h-3 inline" /></li>
                        <li>Scroll and tap "Add to Home Screen"</li>
                        <li>Tap "Add" to confirm</li>
                      </ol>
                    </div>,
                    { duration: 10000 }
                  );
                }}
                data-testid="install-banner-btn"
              >
                <Share className="w-4 h-4" />
                <span className="hidden sm:inline">How to Install</span>
                <span className="sm:hidden">Install</span>
              </Button>
            ) : isInstallable ? (
              <Button
                size="sm"
                variant="secondary"
                className="bg-white text-emerald-600 hover:bg-white/90 gap-1.5 text-xs sm:text-sm"
                onClick={async () => {
                  const success = await promptInstall();
                  if (success) {
                    toast.success('App installed! You can now access Brioritizer from your desktop.');
                    onDismiss();
                  }
                }}
                data-testid="install-banner-btn"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Install App</span>
                <span className="sm:hidden">Install</span>
              </Button>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                className="bg-white text-emerald-600 hover:bg-white/90 gap-1.5 text-xs sm:text-sm"
                onClick={() => {
                  toast.info(
                    <div className="space-y-2">
                      <p className="font-semibold">Add to Desktop:</p>
                      <ul className="text-sm space-y-1">
                        <li><strong>Chrome/Edge:</strong> Click ⋮ menu → "Install Brioritizer"</li>
                        <li><strong>Safari:</strong> File → "Add to Dock"</li>
                      </ul>
                    </div>,
                    { duration: 8000 }
                  );
                }}
                data-testid="install-banner-btn"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Add Shortcut</span>
                <span className="sm:hidden">Add</span>
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/20 p-1 h-8 w-8"
              onClick={onDismiss}
              data-testid="install-banner-dismiss"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
